<template>
  <div class="c-resume__wrapper">
    <div class="c-heroimage">
      <img src="images/banner.jpg" />
      <div class="c-caption">
        Conducting a storyboarding exercise with a group of product managers. Photo credit: Diana
        Freeman-Baer
      </div>
    </div>
    <div class="c-resume">
      <div class="c-block">
        <h3>EXPERIENCE</h3>

        <div class="o-layout">
          <div class="c-job__header o-layout__item u-1/1 u-2/3@sm">
            <div class="c-job__byline">
              <span class="c-job__company">PTC ServiceMax</span>
              <div class="c-job__title">Senior Product Designer</div>
              <!--
              <div class="c-job__title">Senior Digital Product Designer</div>
              -->
            </div>
            <div class="c-job__details">
              <span class="c-job__dates">July 2024 - Present</span>
              <span class="c-job__location">San Ramon, CA</span>
            </div>
          </div>
        </div>

        <div class="o-layout">
          <div class="o-layout__item u-1/1 u-2/3@sm">
            <div class="c-job">
              <div class="c-job__description">
                <p>
                  The ServiceMax division of PTC makes software for teams who repair and maintain industrial machinery
                  and medical devices.
                </p>
                <p>
                  At one point, I was balancing 5 different projects with as many PMs, across 3 different products. This
                  involved both updating and redesigning legacy features and functionality in the Core product as well
                  as leading design on a new product offering.
                </p>
                <p>
                  The new product uses uses Generative AI to navigate complex data structures and Machine Learning to
                  predict outcomes based on historical data.
                </p>
                <p>
                  I have also been exploring AI both as a design tool and as a feature. I co-chaired a working group
                  on developing best practices for incorporating AI, and Ive been using design-to-code and vibe coding
                  tools to rapidly prototype complex interactions, getting feedback quicker and improving communication
                  with Engineering teams.
                </p>
              </div>
            </div>
          </div>
          <div class="o-layout__item u-1/1 u-1/3@sm c-portfolio-links">
            <div class="c-portfolio-link">
              <div class="c-piece__summary">
                <h5>
                  For examples of my recent work, <br />
                  <a href="https://www.linkedin.com/in/michaelcohnmoreau/" target="_blank">
                    please reach out to me directly.
                  </a>
                </h5>
              </div>
              <a href="https://www.linkedin.com/in/michaelcohnmoreau/">
                <img src="images/portfolio/capacity_planning/placeholder.png" />
              </a>
            </div>
          </div>
        </div>

        <div class="o-layout">
          <div class="c-job__header o-layout__item u-1/1 u-2/3@sm">
            <div class="c-job__byline">
              <span class="c-job__company">RefleXion Medical</span>
              <div class="c-job__title">Senior User Experience Designer</div>
            </div>
            <div class="c-job__details">
              <span class="c-job__dates">March 2020 - December 2023</span>
              <span class="c-job__location">Hayward, CA</span>
            </div>
          </div>
        </div>

        <div class="o-layout">
          <div class="o-layout__item u-1/1 u-2/3@sm">
            <div class="c-job">
              <div class="c-job__description">
                <p>
                  RefleXion is a medical device/health tech start-up building a radiation oncology medical device
                  that uses a novel method for tracking tumors.
                </p>
                <p>
                  While at RefleXion, I led the design of a self-service portal called the Medical
                  Physics Workspace. I was in charge of the entire design process, from conducting
                  research with clinical care teams at Stanford, to building workflows and
                  wireframes, and running workshops with end users. I worked closely with a
                  cross-functional team including Product Management and Software to define the
                  product strategy and understand technical feasibility. The workspace is used every
                  day at customer sites, a fact I am very proud of.
                </p>
                <p>
                  In addition, I led design effort on usability testing and hazard analysis to get FDA
                  clearance on a new software release, and designed improvements to features for
                  treatment planning and delivery, increasing customer satisfaction and ease-of-use
                  scores.
                </p>
                <p>
                  My most memorable experience, though, was seeing someone treated for cancer on the
                  system that I had worked on.
                </p>
              </div>
            </div>
          </div>
          <div class="o-layout__item u-1/1 u-1/3@sm c-portfolio-links">
            <router-link to="/portfolio/MedPhysics">
              <div class="c-portfolio-link">
                <div class="c-piece__summary">
                  <h5>End-to-End Product Design for Testing a Medical Device</h5>
                </div>
                <img src="images/portfolio/rx/water_tank_cropped.jpg" />
              </div>
            </router-link>
            <router-link to="/portfolio/Evaluation">
              <div class="c-portfolio-link">
                <div class="c-piece__summary">
                  <h5>Redesigning Treatment Planning and Evaluation Software</h5>
                </div>
                <img src="images/portfolio/rx/inuse_small.jpg" />
              </div>
            </router-link>
          </div>
        </div>

        <div class="o-layout">
          <div class="c-job__header o-layout__item u-1/1 u-2/3@sm">
            <div class="c-job__byline">
              <span class="c-job__company">OSIsoft</span>
              <div class="c-job__title">Senior User Experience Designer</div>
            </div>
            <div class="c-job__details">
              <span class="c-job__dates">July 2018 - March 2020</span>
              <span class="c-job__location">San Leandro, CA</span>
            </div>
          </div>
        </div>

        <div class="o-layout">
          <div class="o-layout__item u-1/1 u-2/3@sm">
            <div class="c-job">
              <div class="c-job__description">
                <p>
                  OSIsoft's primary software product is a Time Series database for industrial machinery,
                  typically installed on-premise.
                </p>
                <p>
                  When OSIsoft wanted to open up new markets for remote operations and fleet
                  management, I was pulled in to help with this new SaaS offering, dubbed Canvas. I
                  ran a Participatory Design workshop (a Research through Design methodology) with
                  SMEs and Engineers to speed up the design process and get us to initial designs
                  faster.
                </p>
                <p>
                  In addition, I worked on a new web-based Design System, and authored a framework
                  for adapting our legacy applications for mobile.
                </p>
              </div>
            </div>
          </div>
          <div class="o-layout__item u-1/1 u-1/3@sm c-portfolio-links">
            <router-link to="/portfolio/Canvas">
              <div class="c-portfolio-link">
                <div class="c-piece__summary">
                  <h5>Mobile-Friendly Remote Ops</h5>
                </div>
                <img src="images/portfolio/canvas/Master.png" />
              </div>
            </router-link>
          </div>
        </div>

        <div class="o-layout">
          <div class="c-job__header o-layout__item u-1/1 u-2/3@sm">
            <div class="c-job__byline">
              <span class="c-job__company">GE Digital</span>
              <div class="c-job__title">Staff User Experience Designer</div>
            </div>
            <div class="c-job__details">
              <span class="c-job__dates">June 2014 - May 2018</span>
              <span class="c-job__location">San Ramon, CA</span>
            </div>
          </div>
        </div>

        <div class="o-layout">
          <div class="o-layout__item u-1/1 u-2/3@sm">
            <div class="c-job">
              <div class="c-job__description">
                <p>
                  At GE, I had the opportunity to work across multiple divisions which meant
                  doing research and design in a variety of places including a jet engine repair
                  shop, a power utility control room, a locomotive cab, and a wind farm.
                </p>
                <p>
                  My primary focus was Predix, GE’s cloud platform for the Industrial Internet of
                  Things (IIoT). For one project, I led design on a software solution for automatic
                  testing and deployment of applications (CI/CD) with the goal of increasing
                  customer adoption.
                </p>
                <p>
                  As a side project, I created a systems map to help explain how all the different
                  parts of the platform worked together. It was called "The best view of Predix I've
                  ever seen" by the CEO of GE Digital.
                </p>
                <p>
                  One of the most fun projects I worked on was the Next Generation Locomotive Cab. I
                  had the idea to create a paper prototype of the cab using giant foam blocks, paper
                  and sticky notes for the controls, and a whiteboard for the window. It was a lot
                  of fun and we got a great reaction from the participants.
                </p>
              </div>
            </div>
          </div>
          <div class="o-layout__item u-1/1 u-1/3@sm c-portfolio-links">
            <router-link to="/portfolio/CICD">
              <div class="c-portfolio-link">
                <div class="c-piece__summary">
                  <h5>Simplifying Deployment to a Cloud Platform</h5>
                </div>
                <img src="images/portfolio/cicd/Solano_RepoPage.png" />
              </div>
            </router-link>
            <router-link to="/portfolio/DataJourney">
              <div class="c-portfolio-link">
                <div class="c-piece__summary">
                  <h5>Explaining Product Value through Handmade Illustrations</h5>
                </div>
                <img src="images/portfolio/data_journey/Data_Journey.jpeg" />
              </div>
            </router-link>
          </div>
        </div>
      </div>

      <div class="c-block">
        <h3>EDUCATION</h3>

        <div class="c-job u-3/5@sm">
          <div class="c-job__header">
            <div class="c-job__title">Master of Science in Human-Computer Interaction</div>
            <div class="c-education__city">Bloomington, Indiana</div>
            <div class="c-education__school">Indiana University</div>
          </div>
        </div>

        <div class="c-job u-3/5@sm">
          <div class="c-job__header">
            <div class="c-job__title">Bachelor of Arts in Computer Science and English</div>
            <div class="c-education__city">Cleveland, Ohio</div>
            <div class="c-education__school">Case Western Reserve University</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '../assets/scss/_mixins.scss';

.c-resume {
  max-width: 1440px;
  margin-left: auto;
  margin-right: auto;
  font-size: mixins.$font-size;
}

.c-job {
  padding-bottom: 1rem;
}

.c-job__final {
  padding-bottom: 0;
}

.c-job__header {
  padding-top: 0.5rem;
}

.c-job__company {
  float: right;
  font-style: italic;
}

.c-job__location,
.c-education__city {
  float: right;
  font-weight: normal;
}

.c-job__title {
  font-weight: bold;
}

.c-job__dates {
  font-weight: normal;
}

.c-education__school {
  font-style: italic;
}

.c-job__description {
  padding-top: 0.5rem;
  text-align: justify;

  p {
    margin-bottom: 0.5rem;
  }
}

.c-portfolio-links {
  padding-left: 2rem;
  padding-top: 0.5rem;

  h3,
  h4 {
    padding-top: 0;
  }

  img {
    width: 100%;
    max-height: 180px;
    object-fit: cover;
    object-position: 100% 50%;

    @include mixins.pop;

    &:hover {
      @include mixins.drop;
    }
  }
}

.c-portfolio-link {
  padding-bottom: 1rem;
}

@media screen and (min-width: 720px) {
  .c-job__dates {
    text-align: right;
  }
}
</style>
